import { type AccommodationConfig, PALETTE_SIZE } from '@poi-planner/shared';

/** JS-accessible palette mirroring the CSS custom properties */
export interface PaletteEntry {
  bg: string;
  header: string;
  border: string;
  text: string;
  sleepBg: string;
  sleepBorder: string;
  sleepText: string;
  badgeBg: string;
  badgeText: string;
}

const PALETTE: PaletteEntry[] = [
  /* 0 – blue */    { bg: '#f0f7ff', header: '#dbeafe', border: '#bfdbfe', text: '#1e40af', sleepBg: '#eff6ff', sleepBorder: '#bfdbfe', sleepText: '#1e3a5f', badgeBg: '#dbeafe', badgeText: '#1e40af' },
  /* 1 – green */   { bg: '#f0fdf4', header: '#dcfce7', border: '#bbf7d0', text: '#166534', sleepBg: '#f0fdf4', sleepBorder: '#bbf7d0', sleepText: '#14532d', badgeBg: '#dcfce7', badgeText: '#166534' },
  /* 2 – red */     { bg: '#fef3f2', header: '#fde8e5', border: '#fecaca', text: '#991b1b', sleepBg: '#fef3f2', sleepBorder: '#fecaca', sleepText: '#7f1d1d', badgeBg: '#fde8e5', badgeText: '#991b1b' },
  /* 3 – yellow */  { bg: '#fefce8', header: '#fef9c3', border: '#fde68a', text: '#854d0e', sleepBg: '#fefce8', sleepBorder: '#fde68a', sleepText: '#713f12', badgeBg: '#fef9c3', badgeText: '#854d0e' },
  /* 4 – purple */  { bg: '#faf5ff', header: '#f3e8ff', border: '#e9d5ff', text: '#6b21a8', sleepBg: '#faf5ff', sleepBorder: '#e9d5ff', sleepText: '#581c87', badgeBg: '#f3e8ff', badgeText: '#6b21a8' },
  /* 5 – pink */    { bg: '#fff1f2', header: '#ffe4e6', border: '#fecdd3', text: '#9f1239', sleepBg: '#fff1f2', sleepBorder: '#fecdd3', sleepText: '#881337', badgeBg: '#ffe4e6', badgeText: '#9f1239' },
  /* 6 – teal */    { bg: '#ecfdf5', header: '#d1fae5', border: '#a7f3d0', text: '#065f46', sleepBg: '#ecfdf5', sleepBorder: '#a7f3d0', sleepText: '#064e3b', badgeBg: '#d1fae5', badgeText: '#065f46' },
  /* 7 – sky */     { bg: '#f0f9ff', header: '#e0f2fe', border: '#bae6fd', text: '#075985', sleepBg: '#f0f9ff', sleepBorder: '#bae6fd', sleepText: '#0c4a6e', badgeBg: '#e0f2fe', badgeText: '#075985' },
];

const DEFAULT_PALETTE: PaletteEntry = {
  bg: '#f0f2f4', header: '#ffffff', border: '#e5e7eb', text: '#617589',
  sleepBg: '#eff6ff', sleepBorder: '#bfdbfe', sleepText: '#1e3a5f',
  badgeBg: '#e5e7eb', badgeText: '#617589',
};

/**
 * Returns the numeric palette index from accommodation config.
 * Returns -1 if no config provided.
 */
export function getAccommodationColorIndex(
  config: AccommodationConfig | undefined,
): number {
  if (!config) return -1;
  const color = config.color;
  if (color >= 0 && color < PALETTE_SIZE) return color;
  return 0;
}

/**
 * Returns the CSS class for an accommodation's color based on config.
 */
export function getAccommodationColorClass(
  config: AccommodationConfig | undefined,
): string {
  const index = getAccommodationColorIndex(config);
  if (index === -1) return 'accom-color-default';
  return `accom-color-${index}`;
}

/**
 * Returns the palette entry for an accommodation (JS values, not CSS vars).
 */
export function getAccommodationPalette(
  config: AccommodationConfig | undefined,
): PaletteEntry {
  const index = getAccommodationColorIndex(config);
  if (index === -1) return DEFAULT_PALETTE;
  return PALETTE[index];
}
